// ============================================================
// TelaTecnico.js — a área do técnico e do auxiliar
//
// Estava dentro do App.js, ao lado do roteamento do portal inteiro. Saiu
// para cá pelo mesmo motivo que a tela do preparador já estava: é uma tela,
// não uma rota, e tela que não dá para renderizar sozinha também não dá
// para conferir antes de liberar.
//
// O que o técnico e o auxiliar veem é quase a mesma coisa. Duas diferenças,
// e as duas vêm de fora, por prop:
//
//   podeCorrigir    o técnico DECIDE a justificativa da modalidade dele; o
//                   auxiliar consulta as faltas já abonadas.
//   semComparativo  o comparativo entre modalidades é leitura de gestão.
// ============================================================

import { useState, useEffect } from "react";
import { carregarSolicitacoesPendentes } from "../firebase";
import { impedimentoDoTecnico } from "./SecaoSolicitacoes";
import ProximasCompeticoes from "../componentes/ProximasCompeticoes";
import SecaoTecnico from "./SecaoTecnico";
import SecaoPresencaFisica from "./SecaoPresencaFisica";
import SecaoComparativo from "./SecaoComparativo";
import SecaoSolicitacoes from "./SecaoSolicitacoes";
import SecaoComprovantes from "./SecaoComprovantes";
import SecaoTreinosExternos from "./SecaoTreinosExternos";
import SecaoPlanejamento from "./SecaoPlanejamento";
import SecaoResultados from "./SecaoResultados";
import RegistroPortal from "../componentes/RegistroPortal";
import CorrecaoDePresenca from "../componentes/CorrecaoDePresenca";
import { Abas } from "../componentes";

export default function TelaTecnico({
  uid, userNucleo, modalidade, podeCorrigir = false, semComparativo = false,
}) {
  const [aba, setAba] = useState("registro");

  // Quantas justificativas esperam a decisão DELE.
  //
  // O coordenador tem esse número no menu desde sempre; o técnico não
  // tinha nada, e só descobria que havia pedido na fila abrindo a aba. A
  // diferença é entre decidir no mesmo dia e decidir na semana seguinte.
  //
  // Conta só o que ele pode resolver: pedido de outra modalidade, pedido
  // que ele mesmo abriu, aviso sobre registro de presença e treino fora do
  // prazo são da coordenação, e somá-los aqui prometeria um trabalho que
  // não é dele.
  //
  // A consulta é a MESMA que a aba faz, e fica guardada por um minuto —
  // então o selo não custa leitura nenhuma a mais.
  const [esperando, setEsperando] = useState(0);
  useEffect(() => {
    if (!podeCorrigir || !userNucleo) { setEsperando(0); return undefined; }
    let vivo = true;
    const hojeISO = new Date().toISOString().slice(0, 10);
    carregarSolicitacoesPendentes(userNucleo)
      .then((lista) => {
        if (!vivo) return;
        setEsperando(lista.filter((s) => !impedimentoDoTecnico({
          solicitacao: s, uid, modalidade, hojeISO,
        })).length);
      })
      .catch(() => { if (vivo) setEsperando(0); });
    return () => { vivo = false; };
    // Recontar ao trocar de aba faz o número cair assim que ele resolve.
  }, [userNucleo, modalidade, uid, podeCorrigir, aba]);

  // O registro da própria presença abre a lista: para quem usa iPhone é o
  // único caminho, e "Registro iOS" dizia o aparelho em vez de dizer a ação.
  //
  // Justificativas vem antes do comparativo porque tem fila esperando por
  // decisão; comparativo é leitura, e leitura espera.
  const tabs = [
    { id: "registro", icone: "rosto", label: "Meu registro" },
    { id: "atletas", icone: "usuarios", label: "Meus atletas" },
    { id: "presenca", icone: "calendarioCheck", label: "Presença" },
    { id: "treinos_externos", icone: "pino", label: "Treinos externos" },
    { id: "comprovantes", icone: "prancheta", label: podeCorrigir ? "Justificativas" : "Faltas abonadas",
      selo: podeCorrigir ? esperando : 0 },
    { id: "planejamento", icone: "grade", label: "Planejamento" },
    // Vem logo depois do planejamento porque é a continuação dele: as
    // provas do trimestre são cadastradas no plano, e é aqui que a
    // colocação de cada atleta entra depois que a prova acontece.
    { id: "resultados", icone: "trofeu", label: "Competições e resultados" },
    ...(semComparativo ? [] : [{ id: "comparativo", icone: "linha", label: "Comparativo" }]),
  ];

  return (
    <div>
      <Abas abas={tabs} ativa={aba} onEscolher={setAba} />

      {aba === "atletas" && <SecaoTecnico userNucleo={userNucleo} modalidade={modalidade} />}
      {/* A grade mostra quem registrou; o formulário abaixo é para quem
          estava no treino e ficou sem registro. Só o técnico pede: o
          auxiliar acompanha o treino e não responde pela frequência. */}
      {aba === "presenca" && (
        <>
          <SecaoPresencaFisica userNucleo={userNucleo} modalidadeFixa={modalidade} podeMarcar={false} />
          {podeCorrigir && <CorrecaoDePresenca userNucleo={userNucleo} modalidade={modalidade} />}
        </>
      )}
      {aba === "comparativo" && <SecaoComparativo userNucleo={userNucleo} isAdmin={false} modalidadeFixa={modalidade} />}

      {/* Quem decide vê a mesa de decisão; quem não decide vê o que já foi
          abonado, sem o motivo, que é dado de saúde na maioria dos casos. */}
      {aba === "comprovantes" && (
        podeCorrigir
          ? <SecaoSolicitacoes userNucleo={userNucleo} papel="tecnico" modalidadeFixa={modalidade} meuUid={uid} />
          : <SecaoComprovantes userNucleo={userNucleo} modalidade={modalidade} somenteAbonadas={true} />
      )}

      {aba === "planejamento" && <SecaoPlanejamento userNucleo={userNucleo} />}
      {aba === "resultados" && (
        <SecaoResultados nucleo={userNucleo} modalidadeFixa={modalidade} />
      )}
      {aba === "treinos_externos" && <SecaoTreinosExternos userNucleo={userNucleo} modalidade={modalidade} ehCoordenacao={false} />}
      {aba === "registro" && <RegistroPortal uid={uid} userNucleo={userNucleo} modoEquipe={true} />}

      {/* A agenda do que ainda vai acontecer, no fim da página.
          São as mesmas competições cadastradas em Competições e
          resultados: não há segunda agenda a manter. Quem vê o quê é
          decidido no servidor. */}
      <ProximasCompeticoes />
    </div>
  );
}
